import React, { useRef } from 'react';

import { useTheme } from '@mui/material/styles';
import { Box, Chip, Avatar, Tooltip, Typography, ListItemText } from '@mui/material';

import { useTranslate } from 'src/locales';
import { truncateText } from 'src/pages/chat/utils/get-message';

import { Iconify } from 'src/components/iconify';
import { Field } from 'src/components/hook-form';
import { SearchNotFound } from 'src/components/search-not-found';

interface Recipient {
  id: string;
  name: string;
  avatarUrl?: string;
}

interface CustomAutocompleteProps {
  name: string;
  value?: Recipient | null;
  // onChange: (value: Recipient | null) => void;
  isOptionEqualToValue: (option: Recipient, value: Recipient) => boolean;
  placeholder?: string;
  label: string;
  options: Recipient[];
  listScrollHandler: (event: React.UIEvent<HTMLUListElement>) => void;
  searchValue: string;
  searchValueHandler: (value: string) => void;
  open: boolean;
  openListHandler: () => void;
  closeListHandler: () => void;
  isFetching: boolean;
  onChangeField?: ({ value, fieldName }: { value: string; fieldName: string }) => void;
}

export const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  name,
  isOptionEqualToValue,
  placeholder = '+ Recipients',
  label,
  options,
  listScrollHandler,
  searchValue,
  searchValueHandler,
  open,
  openListHandler,
  closeListHandler,
  isFetching,
  onChangeField,
}) => {
  const theme = useTheme();
  const { t } = useTranslate('translation');

  const listboxRef = useRef<HTMLUListElement | null>(null);

  const primaryText = (assignee: any) => {
    const displayText = assignee?.name?.trim() || assignee?.email || '';

    if (displayText?.length > 20) {
      return (
        <Tooltip title={displayText}>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {truncateText(displayText, 20)}
          </Typography>
        </Tooltip>
      );
    }

    return displayText;
  };

  return (
    <Field.Autocomplete
      name={name}
      label={label}
      popupIcon={null}
      options={options}
      open={open}
      onChangeField={onChangeField}
      onOpen={openListHandler}
      onClose={closeListHandler}
      loading={isFetching}
      // onChange={(e, newValue) => onChange(newValue)}
      onInputChange={(e, newVal, reason) => {
        if (reason === 'input') searchValueHandler(newVal);
      }}
      getOptionLabel={(option) => option?.name ?? option}
      isOptionEqualToValue={isOptionEqualToValue}
      placeholder={placeholder}
      noOptionsText={<SearchNotFound query={searchValue} />}
      slotProps={{
        listbox: {
          ref: listboxRef,
          onScroll: listScrollHandler,
        },
        textfield: { slotProps: { inputLabel: { shrink: true } } },
      }}
      filterOptions={(x) => x}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Box
            sx={{
              mr: 1,
              width: 32,
              height: 32,
              overflow: 'hidden',
              borderRadius: '50%',
              position: 'relative',
            }}
          >
            <Avatar alt={option.name} src={option.ProfileURL ?? ''} sx={{ width: 1, height: 1 }} />
            <Box
              sx={{
                top: 0,
                left: 0,
                width: 1,
                height: 1,
                opacity: selected ? 1 : 0,
                display: 'flex',
                position: 'absolute',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.grey[900],
                color: 'primary.main',
                transition: 'opacity 150ms ease-in-out',
              }}
            >
              <Iconify icon="eva:checkmark-fill" />
            </Box>
          </Box>
          <ListItemText
            primary={primaryText(option)}
            secondary={option.isUser ? t('USER') : t('TEAM')}
            sx={{ flex: 1, pr: 1 }}
            slotProps={{
              secondary: { sx: { typography: 'caption', color: 'text.disabled' } },
            }}
          />
        </li>
      )}
      renderTags={(selected, getTagProps) =>
        selected?.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={index}
            label={option.name}
            avatar={<Avatar alt={option.name} src={option.avatarUrl} />}
            size="small"
            variant="soft"
          />
        ))
      }
    />
  );
};
